/**
 * Конфигурация и ядро расчёта калькулятора металлоконструкций.
 *
 * Модель ценообразования и справочники снеговых/ветровых районов воспроизводят
 * публичный калькулятор met-panel.ru/3d-configurator (реверс-инжиниринг,
 * проверено по контрольному примеру 18×30×6, Москва, 4 окна, 2 ворот → 8 461 000 ₽).
 *
 * Файл фреймворк-независим (без React) — поэтому ядро расчёта легко тестируется.
 */

export interface CityLoad {
  name: string;
  /** Снеговой район (СП 20.13330), 1..8 */
  snow: number;
  /** Ветровой район (СП 20.13330), 1..7 */
  wind: number;
}

export interface RalColor {
  /** Код RAL */
  ral: string;
  hex: string;
  name: string;
}

export type RoofType = 'single' | 'double';

export interface CalculatorState {
  cityName: string;
  /** Ширина, м */
  width: number;
  /** Длина, м */
  length: number;
  /** Высота, м */
  height: number;
  roof: RoofType;
  /** Тёплое исполнение (сэндвич) — true / холодное (профлист) — false */
  warm: boolean;
  /** Кран-балка 3.2т (усиление каркаса) */
  crane: boolean;
  /** Кол-во окон */
  windows: number;
  /** Кол-во ворот */
  gates: number;
  /** Код RAL */
  ral: string;
}

export interface Estimate {
  /** Металлокаркас, ₽ */
  steel: number;
  /** Ограждающие конструкции, ₽ */
  wall: number;
  /** Окна и ворота, ₽ */
  ops: number;
  /** Итого, ₽ */
  total: number;
  /** Снеговой район выбранного города */
  snow: number;
  /** Ветровой район выбранного города */
  wind: number;
  /** Площадь основания, м² */
  area: number;
  /** Площадь ограждающих стен, м² */
  wallArea: number;
  /** Итоговый коэффициент нагрузки */
  k: number;
}

/** Коэффициенты ценообразования (как у референса). */
export const PRICING = {
  /** Цена металлокаркаса за м² основания */
  steelPerSqm: 8500,
  /** Ограждающие — тёплое исполнение, ₽/м² */
  wallWarmPerSqm: 4800,
  /** Ограждающие — холодное исполнение, ₽/м² */
  wallColdPerSqm: 2100,
  /** Цена за одно окно */
  windowPrice: 25000,
  /** Цена за одни ворота */
  gatePrice: 90000,
  /** Надбавка коэффициента за единицу снегового района */
  snowCoeff: 0.05,
  /** Надбавка коэффициента за единицу ветрового района */
  windCoeff: 0.03,
  /** Надбавка коэффициента за кран-балку (усиление) */
  craneCoeff: 0.2,
} as const;

export type CalculatorPricing = { [Key in keyof typeof PRICING]: number };
export interface CalculatorSettings {
  pricing: CalculatorPricing;
  revision: number;
  updatedAt: string | null;
}

export const WIDTH_OPTIONS = [12, 15, 18, 21, 24];
export const LENGTH_OPTIONS = [18, 24, 30, 36, 42, 48, 54, 60, 66, 72, 78, 84];
export const HEIGHT_OPTIONS = [4, 6, 8, 10, 12];
export const WINDOWS_MAX = 40;
export const GATES_MAX = 5;

/** Бренд и контакты для шапки калькулятора и КП. */
export const BRAND = {
  name: 'ЗМК Континенталь',
  version: 'V.1.0',
  phone: '+7 (958) 761-74-85',
  email: 'sales@ur-met.ru',
  site: 'ur-met.ru',
} as const;

export const RAL_COLORS: RalColor[] = [
  { ral: '5005', hex: '#18458b', name: 'Синий' },
  { ral: '9003', hex: '#ffffff', name: 'Белый' },
  { ral: '7004', hex: '#9ca3af', name: 'Серый' },
  { ral: '7024', hex: '#4b5563', name: 'Графит' },
  { ral: '3005', hex: '#7f1d1d', name: 'Красный' },
  { ral: '6005', hex: '#064e3b', name: 'Зеленый' },
  { ral: '8017', hex: '#451a03', name: 'Коричневый' },
  { ral: '1015', hex: '#fef3c7', name: 'Бежевый' },
];

export const CITIES: CityLoad[] = [
  { name: 'Москва', snow: 3, wind: 1 },
  { name: 'Санкт-Петербург', snow: 3, wind: 2 },
  { name: 'Новосибирск', snow: 4, wind: 3 },
  { name: 'Екатеринбург', snow: 4, wind: 2 },
  { name: 'Казань', snow: 4, wind: 2 },
  { name: 'Нижний Новгород', snow: 4, wind: 2 },
  { name: 'Челябинск', snow: 3, wind: 2 },
  { name: 'Самара', snow: 4, wind: 2 },
  { name: 'Омск', snow: 3, wind: 3 },
  { name: 'Ростов-на-Дону', snow: 2, wind: 3 },
  { name: 'Уфа', snow: 5, wind: 2 },
  { name: 'Красноярск', snow: 3, wind: 3 },
  { name: 'Воронеж', snow: 3, wind: 2 },
  { name: 'Пермь', snow: 5, wind: 2 },
  { name: 'Волгоград', snow: 2, wind: 3 },
  { name: 'Краснодар', snow: 2, wind: 3 },
  { name: 'Саратов', snow: 3, wind: 3 },
  { name: 'Тюмень', snow: 4, wind: 2 },
  { name: 'Тольятти', snow: 4, wind: 2 },
  { name: 'Ижевск', snow: 4, wind: 2 },
  { name: 'Барнаул', snow: 4, wind: 3 },
  { name: 'Ульяновск', snow: 3, wind: 2 },
  { name: 'Иркутск', snow: 3, wind: 2 },
  { name: 'Хабаровск', snow: 2, wind: 3 },
  { name: 'Ярославль', snow: 4, wind: 2 },
  { name: 'Владивосток', snow: 2, wind: 4 },
  { name: 'Махачкала', snow: 1, wind: 4 },
  { name: 'Томск', snow: 4, wind: 2 },
  { name: 'Оренбург', snow: 3, wind: 3 },
  { name: 'Кемерово', snow: 4, wind: 2 },
  { name: 'Новокузнецк', snow: 4, wind: 2 },
  { name: 'Рязань', snow: 3, wind: 2 },
  { name: 'Астрахань', snow: 2, wind: 3 },
  { name: 'Набережные Челны', snow: 4, wind: 2 },
  { name: 'Пенза', snow: 3, wind: 2 },
  { name: 'Липецк', snow: 3, wind: 2 },
  { name: 'Киров', snow: 4, wind: 2 },
  { name: 'Чебоксары', snow: 4, wind: 2 },
  { name: 'Тула', snow: 3, wind: 2 },
  { name: 'Калининград', snow: 2, wind: 2 },
  { name: 'Балашиха', snow: 3, wind: 1 },
  { name: 'Курск', snow: 3, wind: 2 },
  { name: 'Ставрополь', snow: 2, wind: 4 },
  { name: 'Улан-Удэ', snow: 2, wind: 3 },
  { name: 'Севастополь', snow: 1, wind: 4 },
  { name: 'Тверь', snow: 3, wind: 2 },
  { name: 'Магнитогорск', snow: 3, wind: 2 },
  { name: 'Сочи', snow: 1, wind: 4 },
  { name: 'Иваново', snow: 4, wind: 2 },
  { name: 'Брянск', snow: 3, wind: 2 },
  { name: 'Белгород', snow: 2, wind: 2 },
  { name: 'Сургут', snow: 4, wind: 2 },
  { name: 'Владимир', snow: 3, wind: 2 },
  { name: 'Нижний Тагил', snow: 4, wind: 2 },
  { name: 'Архангельск', snow: 4, wind: 2 },
  { name: 'Чита', snow: 2, wind: 3 },
  { name: 'Калуга', snow: 3, wind: 2 },
  { name: 'Симферополь', snow: 1, wind: 3 },
  { name: 'Смоленск', snow: 3, wind: 2 },
  { name: 'Волжский', snow: 2, wind: 3 },
  { name: 'Саранск', snow: 3, wind: 2 },
  { name: 'Череповец', snow: 4, wind: 1 },
  { name: 'Курган', snow: 3, wind: 2 },
  { name: 'Вологда', snow: 4, wind: 1 },
  { name: 'Орёл', snow: 3, wind: 2 },
  { name: 'Владикавказ', snow: 2, wind: 4 },
  { name: 'Подольск', snow: 3, wind: 1 },
  { name: 'Грозный', snow: 1, wind: 4 },
  { name: 'Мурманск', snow: 5, wind: 5 },
  { name: 'Тамбов', snow: 3, wind: 2 },
  { name: 'Стерлитамак', snow: 5, wind: 2 },
  { name: 'Петрозаводск', snow: 4, wind: 2 },
  { name: 'Кострома', snow: 4, wind: 2 },
  { name: 'Нижневартовск', snow: 4, wind: 2 },
  { name: 'Новороссийск', snow: 2, wind: 5 },
  { name: 'Йошкар-Ола', snow: 4, wind: 2 },
  { name: 'Таганрог', snow: 2, wind: 4 },
  { name: 'Комсомольск-на-Амуре', snow: 3, wind: 2 },
  { name: 'Химки', snow: 3, wind: 1 },
  { name: 'Сыктывкар', snow: 5, wind: 2 },
  { name: 'Нальчик', snow: 2, wind: 3 },
  { name: 'Нижнекамск', snow: 4, wind: 2 },
  { name: 'Шахты', snow: 2, wind: 3 },
  { name: 'Дзержинск', snow: 4, wind: 2 },
  { name: 'Братск', snow: 4, wind: 2 },
  { name: 'Орск', snow: 3, wind: 3 },
  { name: 'Ангарск', snow: 3, wind: 2 },
  { name: 'Энгельс', snow: 3, wind: 3 },
  { name: 'Благовещенск', snow: 2, wind: 2 },
  { name: 'Старый Оскол', snow: 2, wind: 2 },
  { name: 'Великий Новгород', snow: 3, wind: 2 },
  { name: 'Королёв', snow: 3, wind: 1 },
  { name: 'Псков', snow: 3, wind: 2 },
  { name: 'Мытищи', snow: 3, wind: 1 },
  { name: 'Бийск', snow: 4, wind: 2 },
  { name: 'Люберцы', snow: 3, wind: 1 },
  { name: 'Прокопьевск', snow: 4, wind: 2 },
  { name: 'Южно-Сахалинск', snow: 7, wind: 5 },
  { name: 'Балаково', snow: 3, wind: 3 },
  { name: 'Рыбинск', snow: 4, wind: 2 },
  { name: 'Армавир', snow: 2, wind: 3 },
  { name: 'Северодвинск', snow: 4, wind: 3 },
  { name: 'Петропавловск-Камчатский', snow: 7, wind: 7 },
  { name: 'Абакан', snow: 3, wind: 3 },
  { name: 'Норильск', snow: 5, wind: 5 },
  { name: 'Уссурийск', snow: 2, wind: 3 },
  { name: 'Находка', snow: 2, wind: 4 },
  { name: 'Волгодонск', snow: 2, wind: 3 },
  { name: 'Березники', snow: 5, wind: 2 },
  { name: 'Салават', snow: 4, wind: 2 },
  { name: 'Электросталь', snow: 3, wind: 1 },
  { name: 'Миасс', snow: 3, wind: 2 },
  { name: 'Рубцовск', snow: 4, wind: 3 },
  { name: 'Альметьевск', snow: 4, wind: 2 },
  { name: 'Ковров', snow: 3, wind: 2 },
  { name: 'Коломна', snow: 3, wind: 1 },
  { name: 'Майкоп', snow: 2, wind: 3 },
  { name: 'Пятигорск', snow: 2, wind: 4 },
  { name: 'Одинцово', snow: 3, wind: 1 },
  { name: 'Копейск', snow: 3, wind: 2 },
  { name: 'Хасавюрт', snow: 1, wind: 3 },
  { name: 'Новомосковск', snow: 3, wind: 2 },
  { name: 'Кисловодск', snow: 2, wind: 3 },
  { name: 'Серпухов', snow: 3, wind: 1 },
  { name: 'Первоуральск', snow: 4, wind: 2 },
  { name: 'Новочебоксарск', snow: 4, wind: 2 },
  { name: 'Нефтеюганск', snow: 4, wind: 2 },
  { name: 'Димитровград', snow: 3, wind: 2 },
  { name: 'Нефтекамск', snow: 4, wind: 2 },
  { name: 'Черкесск', snow: 2, wind: 3 },
  { name: 'Орехово-Зуево', snow: 3, wind: 1 },
  { name: 'Дербент', snow: 1, wind: 4 },
  { name: 'Камышин', snow: 3, wind: 3 },
  { name: 'Невинномысск', snow: 2, wind: 4 },
  { name: 'Красногорск', snow: 3, wind: 1 },
  { name: 'Муром', snow: 3, wind: 2 },
  { name: 'Батайск', snow: 2, wind: 3 },
  { name: 'Сергиев Посад', snow: 3, wind: 1 },
  { name: 'Новошахтинск', snow: 2, wind: 3 },
  { name: 'Ноябрьск', snow: 4, wind: 2 },
  { name: 'Щёлково', snow: 3, wind: 1 },
  { name: 'Кызыл', snow: 2, wind: 2 },
  { name: 'Октябрьский', snow: 4, wind: 2 },
  { name: 'Ачинск', snow: 3, wind: 2 },
  { name: 'Северск', snow: 4, wind: 2 },
  { name: 'Новокуйбышевск', snow: 4, wind: 2 },
  { name: 'Елец', snow: 3, wind: 2 },
  { name: 'Арзамас', snow: 4, wind: 2 },
  { name: 'Обнинск', snow: 3, wind: 2 },
  { name: 'Жуковский', snow: 3, wind: 1 },
  { name: 'Новый Уренгой', snow: 5, wind: 3 },
  { name: 'Элиста', snow: 2, wind: 4 },
  { name: 'Пушкино', snow: 3, wind: 1 },
  { name: 'Артём', snow: 2, wind: 4 },
  { name: 'Каспийск', snow: 1, wind: 4 },
  { name: 'Ессентуки', snow: 2, wind: 3 },
];

export const DEFAULT_STATE: CalculatorState = {
  cityName: 'Москва',
  width: 18,
  length: 30,
  height: 6,
  roof: 'double',
  warm: true,
  crane: false,
  windows: 4,
  gates: 2,
  ral: '5005',
};

/** Найти город по имени (с фолбэком на первый город). */
export function getCity(name: string): CityLoad {
  return CITIES.find((c) => c.name === name) ?? CITIES[0];
}

const normalizeCity = (value: string) => value.trim().toLowerCase().replace(/ё/g, 'е');

/**
 * Список городов для выпадающего списка.
 * Пока пользователь не начал печатать (поле пустое или в нём выбранный город),
 * показываем весь список — иначе выбранный город «съедает» остальные варианты.
 * При вводе: сначала совпадения с начала названия, затем по вхождению.
 */
export function filterCities(query: string, selectedName?: string): CityLoad[] {
  const q = normalizeCity(query);
  if (!q || (selectedName && q === normalizeCity(selectedName))) return CITIES;
  const starts = CITIES.filter((c) => normalizeCity(c.name).startsWith(q));
  const contains = CITIES.filter((c) => {
    const name = normalizeCity(c.name);
    return !name.startsWith(q) && name.includes(q);
  });
  return [...starts, ...contains];
}

/** Найти город по введённому тексту без учёта регистра и «ё». */
export function findCityByName(value: string): CityLoad | undefined {
  const q = normalizeCity(value);
  return q ? CITIES.find((c) => normalizeCity(c.name) === q) : undefined;
}

/** Найти цвет RAL по коду (с фолбэком на первый). */
export function getRal(ral: string): RalColor {
  return RAL_COLORS.find((c) => c.ral === ral) ?? RAL_COLORS[0];
}

/**
 * Рассчитать смету по параметрам.
 * Формула воспроизводит met-panel.ru:
 *   area     = width * length
 *   wallArea = (width + length) * 2 * height
 *   k        = 1 + snow*0.05 + wind*0.03 + (crane ? 0.2 : 0)
 *   steel    = round(area * 8500 * k)
 *   wall     = round(wallArea * (warm ? 4800 : 2100))
 *   ops      = windows * 25000 + gates * 90000
 */
export function calcEstimate(state: CalculatorState, pricing: CalculatorPricing = PRICING): Estimate {
  const city = getCity(state.cityName);
  const area = state.width * state.length;
  const wallArea = (state.width + state.length) * 2 * state.height;
  const k =
    1 +
    city.snow * pricing.snowCoeff +
    city.wind * pricing.windCoeff +
    (state.crane ? pricing.craneCoeff : 0);

  const steel = Math.round(area * pricing.steelPerSqm * k);
  const wall = Math.round(
    wallArea * (state.warm ? pricing.wallWarmPerSqm : pricing.wallColdPerSqm),
  );
  const ops = state.windows * pricing.windowPrice + state.gates * pricing.gatePrice;
  const total = steel + wall + ops;

  return { steel, wall, ops, total, snow: city.snow, wind: city.wind, area, wallArea, k };
}

/** Форматирование суммы в рублях (как «8 461 000 ₽»). */
export function formatRub(value: number): string {
  return `${value.toLocaleString('ru-RU')} ₽`;
}

/** Строка детализированной сметы для КП. */
export interface EstimateLine {
  title: string;
  /** Количество */
  qty: number;
  /** Единица измерения */
  unit: 'м²' | 'шт';
  /** Цена за единицу, ₽ */
  price: number;
  /** Коэффициент нагрузок в формате «1,18» (только у металлокаркаса) */
  coeffLabel?: string;
  /** Пояснение к коэффициенту (районы нагрузок, кран-балка) */
  coeffNote?: string;
  /** Сумма по строке, ₽ */
  sum: number;
  /** Состав позиции — что входит в цену */
  includes: string;
  /** Расшифровка количества, коэффициентов и стоимости по фактической формуле. */
  calculation: string[];
}

/**
 * Развернуть смету в детальные строки для КП:
 * количество × цена (× коэффициент) = сумма + состав каждой позиции.
 * Строки окон и ворот появляются только при ненулевом количестве.
 */
export function buildEstimateLines(state: CalculatorState, estimate: Estimate, pricing: CalculatorPricing = PRICING): EstimateLine[] {
  const ral = getRal(state.ral);
  const colorLabel = `RAL ${ral.ral} (${ral.name.toLowerCase()})`;

  const number = (value: number) => value.toLocaleString('ru-RU', { maximumFractionDigits: 12 });
  const coefficient = number(estimate.k);
  const loadFormula = `1 + ${estimate.snow} × ${number(pricing.snowCoeff)} + ${estimate.wind} × ${number(pricing.windCoeff)}` +
    (state.crane ? ` + ${number(pricing.craneCoeff)}` : '');

  const lines: EstimateLine[] = [
    {
      title: 'Металлокаркас',
      qty: estimate.area,
      unit: 'м²',
      price: pricing.steelPerSqm,
      coeffLabel: coefficient,
      coeffNote:
        `снеговой район ${estimate.snow}, ветровой район ${estimate.wind}` +
        (state.crane ? ', усиление под кран-балку 3,2 т' : ''),
      sum: estimate.steel,
      calculation: [
        `Площадь основания: ${number(state.width)} × ${number(state.length)} = ${number(estimate.area)} м².`,
        `Коэффициент нагрузки: ${loadFormula} = ${coefficient}.`,
        `Стоимость: ${number(estimate.area)} м² × ${number(pricing.steelPerSqm)} ₽/м² × ${coefficient} = ${formatRub(estimate.steel)}.`,
      ],
      includes:
        'Колонны и стропильные конструкции из стального профиля, прогоны, связи, ' +
        'анкерные болты, метизы, антикоррозийная обработка (грунт-эмаль).',
    },
    {
      title: 'Стеновые ограждения',
      qty: estimate.wallArea,
      unit: 'м²',
      price: state.warm ? pricing.wallWarmPerSqm : pricing.wallColdPerSqm,
      sum: estimate.wall,
      calculation: [
        `Площадь стен: (${number(state.width)} + ${number(state.length)}) × 2 × ${number(state.height)} = ${number(estimate.wallArea)} м².`,
        `Стоимость: ${number(estimate.wallArea)} м² × ${number(state.warm ? pricing.wallWarmPerSqm : pricing.wallColdPerSqm)} ₽/м² = ${formatRub(estimate.wall)}.`,
      ],
      includes: state.warm
        ? `Стеновые сэндвич-панели в цвете ${colorLabel}, ` +
          'фасонные и доборные элементы, крепёж, уплотнители.'
        : `Стеновой профлист с полимерным покрытием в цвете ${colorLabel}, ` +
          'доборные элементы, крепёж.',
    },
  ];

  if (state.windows > 0) {
    lines.push({
      title: 'Окна ПВХ',
      qty: state.windows,
      unit: 'шт',
      price: pricing.windowPrice,
      sum: state.windows * pricing.windowPrice,
      calculation: [`Стоимость: ${number(state.windows)} шт × ${number(pricing.windowPrice)} ₽/шт = ${formatRub(state.windows * pricing.windowPrice)}.`],
      includes: 'Оконные блоки ПВХ с установкой в проёмы каркаса.',
    });
  }

  if (state.gates > 0) {
    lines.push({
      title: 'Ворота',
      qty: state.gates,
      unit: 'шт',
      price: pricing.gatePrice,
      sum: state.gates * pricing.gatePrice,
      calculation: [`Стоимость: ${number(state.gates)} шт × ${number(pricing.gatePrice)} ₽/шт = ${formatRub(state.gates * pricing.gatePrice)}.`],
      includes: 'Воротные блоки с комплектом фурнитуры и монтажом в проёмы каркаса.',
    });
  }

  return lines;
}

export const ROOF_LABELS: Record<RoofType, string> = {
  single: 'Односкатная',
  double: 'Двускатная',
};
